import { useQuery } from '@tanstack/react-query';

import { MIN_SEARCH_LENGTH } from '@/components/features/UsersList/constants';
import { useAuth } from '@/contexts/AuthContext';
import { createApiClient } from '@/shared/api/apiClient';
import type { components } from '@/shared/api/openapi';

const LIMIT = 50;

export type AdminUserAutocompleteResult = components['schemas']['GetAdminUsersAutocompleteEndpoint_GetUsersResponse'];

export interface UseAdminUsersAutocompleteOptions {
  filter: string;
  enabled?: boolean;
}

export function useAdminUsersAutocomplete({ filter, enabled = true }: UseAdminUsersAutocompleteOptions) {
  const { getAccessToken } = useAuth();

  return useQuery({
    queryKey: ['admin-users-autocomplete', filter],
    queryFn: async ({ signal }) => {
      const client = createApiClient(getAccessToken, {
        cache: 'no-store',
        signal,
      });

      const { data, error } = await client.GET('/api/users/admin/autocomplete', {
        params: {
          query: { Filter: filter, Limit: LIMIT },
        },
      });

      if (error) {
        throw new Error(error.title || 'Failed to fetch admin users');
      }

      return (data ?? []) as AdminUserAutocompleteResult[];
    },
    enabled: enabled && filter.length >= MIN_SEARCH_LENGTH,
    staleTime: 30000,
  });
}
